import { useState } from "react";
import { Alert, Button, Checkbox, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import type { DeletionIntake } from "@/api/accountDeletion";
import { env } from "@/config/env";
import { actionError } from "./presentation";
import type { AccountDeletionQueueViewProps } from "./types";

export function IntakeDialog({
  intake,
  mode,
  currentUpdatedAt,
  canAct,
  pending,
  onClose,
  onVerify,
  onDismiss,
}: {
  intake: DeletionIntake;
  mode: "verify" | "dismiss";
  currentUpdatedAt: string | null;
  canAct: boolean;
  pending: boolean;
  onClose(): void;
  onVerify: AccountDeletionQueueViewProps["onVerifyIntake"];
  onDismiss: AccountDeletionQueueViewProps["onDismissIntake"];
}) {
  const [userId, setUserId] = useState("");
  const [reference, setReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = currentUpdatedAt !== intake.updatedAt;
  const busy = pending || submitting;
  const validUser = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(
    userId.trim(),
  );
  const canSubmit =
    canAct &&
    !changed &&
    !busy &&
    !!reference.trim() &&
    confirmed &&
    (mode === "dismiss" || validUser);

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "verify")
        await onVerify(intake, { userId: userId.trim(), evidenceReference: reference.trim() });
      else await onDismiss(intake, { evidenceReference: reference.trim() });
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened
      onClose={() => {
        if (!busy) onClose();
      }}
      title={mode === "verify" ? "Link verified account" : "Dismiss web intake"}
      centered
      size="lg"
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      withCloseButton={!busy}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack>
          <Text size="sm">
            {mode === "verify"
              ? "Verify account ownership, deletion intent and the follow-up channel in the restricted case before linking. Knowing an email address is not proof of ownership."
              : "Record why this intake is invalid or already resolved. Dismissal removes its unverified contact address."}
          </Text>
          {env.isProd ? <Alert color="red">You are updating an intake in production.</Alert> : null}
          <Text size="sm" c="dimmed">
            Intake {intake.id}
          </Text>
          {mode === "verify" ? (
            <TextInput
              label="Verified account ID"
              placeholder="Account UUID"
              value={userId}
              onChange={(e) => setUserId(e.currentTarget.value)}
              disabled={busy}
              autoComplete="off"
              required
            />
          ) : null}
          <TextInput
            label="Restricted evidence reference"
            description="Reference the verification or dismissal decision. Do not include contact details."
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            disabled={busy}
            maxLength={512}
            required
          />
          <Checkbox
            label={
              mode === "verify"
                ? "I verified this account's ownership, deletion intent and follow-up channel."
                : "I verified the reason for dismissing this intake."
            }
            checked={confirmed}
            onChange={(e) => setConfirmed(e.currentTarget.checked)}
            disabled={busy}
          />
          {changed ? (
            <Alert color="yellow">
              This intake changed or is no longer in the queue. Close the dialog and refresh before
              acting.
            </Alert>
          ) : null}
          {error ? (
            <Alert color="red" role="alert">
              {error}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              color={mode === "dismiss" ? "red" : undefined}
              loading={busy}
              disabled={!canSubmit}
            >
              {mode === "verify" ? "Link account" : "Dismiss intake"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
